<!-- eslint-disable vue/multi-word-component-names -->
<template>
    <div :class="['el-scrollbar', { 'is-inverse': inverse }]">
        <div
            v-if="!native"
            ref="wrap"
            :class="[wrapClass, 'el-scrollbar__wrap', axisClass, { 'el-scrollbar__wrap--hidden-default': !gutter }]"
            :style="wrapStyleComputed"
            @scroll="handleScroll"
        >
            <component
                :is="tag"
                ref="resize"
                :class="['el-scrollbar__view', viewClass]"
                :style="viewStyle"
            >
                <slot />
            </component>
        </div>
        <div
            v-else
            ref="wrap"
            :class="[wrapClass, 'el-scrollbar__wrap']"
            :style="wrapStyleComputed"
        >
            <component
                :is="tag"
                ref="resize"
                :class="['el-scrollbar__view', viewClass]"
                :style="viewStyle"
            >
                <slot />
            </component>
        </div>

        <Bar
            v-if="!native && axis !== 'x' && sizeHeight"
            vertical
            :move="moveY"
            :size="sizeHeight"
            :outer-scroll="outerScroll"
            :dropdown="dropdown"
            :start="offset[0]"
            :end="offset[2]"
        />
        <Bar
            v-if="!native && axis !== 'y' && sizeWidth"
            :move="moveX"
            :size="sizeWidth"
            :outer-scroll="outerScroll"
            :dropdown="dropdown"
            :start="offset[3]"
            :end="offset[1]"
        />
    </div>
</template>

<script setup>
import ResizeObserver from 'resize-observer-polyfill';
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue';
import Bar from './bar.vue';

const props = defineProps({
    native: Boolean,
    wrapStyle: { type: [String, Object, Array], default: '' },
    wrapClass: { type: [String, Object, Array], default: '' },
    viewClass: { type: [String, Object, Array], default: '' },
    viewStyle: { type: [String, Object, Array], default: '' },
    noresize: Boolean,
    tag: { type: String, default: 'div' },
    inverse: Boolean,
    outerScroll: Boolean,
    dropdown: Boolean,
    axis: { type: String, default: '' },
    offset: {
        type: Array,
        default: () => [0, 0, 0, 0],
        validator: val => val.length === 4,
    },
    borderTopWhenScrolled: Boolean,
});

const emit = defineEmits(['scroll', 'overflow-update']);

const wrap = ref(null);
const resize = ref(null);
const gutter = ref(0);
const moveX = ref(0);
const moveY = ref(0);
const sizeWidth = ref('');
const sizeHeight = ref('');

let resizeObserver;

function getScrollbarWidth() {
    if (typeof window === 'undefined') return 0;
    return window.innerWidth - document.documentElement.clientWidth;
}

function addClass(el, cls) {
    el.classList?.add(cls);
}
function removeClass(el, cls) {
    el.classList?.remove(cls);
}

const axisClass = computed(() => {
    switch (props.axis) {
        case 'x': return 'el-scrollbar__wrap--x-axis';
        case 'y': return 'el-scrollbar__wrap--y-axis';
        case 'disabled': return '';
        default: return 'el-scrollbar__wrap--axis';
    }
});

const wrapStyleComputed = computed(() => {
    const gutterSize = gutter.value ?? getScrollbarWidth();
    const gutterWidth = `-${gutterSize}px`;

    if (typeof props.wrapStyle === 'string') {
        let style = props.wrapStyle.trim().replace(/;$/, '');
        if (props.axis !== 'y') style += `; margin-bottom: ${gutterWidth}`;
        if (props.axis !== 'x') style += `; margin-right: ${gutterWidth}`;
        return style;
    }

    const style = Array.isArray(props.wrapStyle)
        ? Object.assign({}, ...props.wrapStyle)
        : typeof props.wrapStyle === 'object'
            ? { ...props.wrapStyle }
            : {};

    if (props.axis !== 'y') style.marginBottom = gutterWidth;
    if (props.axis !== 'x') style.marginRight = gutterWidth;

    return style;
});

function updateScrollPosition() {
    const el = wrap.value;
    if (!el) return;

    if (props.borderTopWhenScrolled) {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        el.scrollTop !== 0 ? addClass(el, 'is-bordered-top') : removeClass(el, 'is-bordered-top');
    }

    const minSize = 40;

    moveY.value = (el.scrollTop * 100) / el.clientHeight;
    moveX.value = (el.scrollLeft * 100) / el.clientWidth;

    const scrollHeight = Math.ceil(parseInt(sizeHeight.value) / 100 * el.clientHeight);
    const scrollWidth = Math.ceil(parseInt(sizeWidth.value) / 100 * el.clientWidth);

    if (scrollHeight <= minSize) {
        moveY.value = (el.scrollTop / (el.scrollHeight - el.clientHeight)) * (el.clientHeight - minSize) / minSize * 100;
    }
    if (scrollWidth <= minSize) {
        moveX.value = (el.scrollLeft / (el.scrollWidth - el.clientWidth)) * (el.clientWidth - minSize) / minSize * 100;
    }
}

function update() {
    const el = wrap.value;
    if (!el) return;

    const minSize = 40;
    const rect = el.getBoundingClientRect();

    const isHeightOverflow = Math.abs(el.clientHeight - el.scrollHeight) > 1;
    const isWidthOverflow = Math.abs(el.clientWidth - el.scrollWidth) > 1;

    const heightAccurate = Math.abs(rect.height - el.offsetHeight) <= 1;
    const widthAccurate = Math.abs(rect.width - el.offsetWidth) <= 1;

    let widthPercent = widthAccurate && isWidthOverflow ? (el.clientWidth * 100 / el.scrollWidth) : 100;
    let heightPercent = heightAccurate && isHeightOverflow ? (el.clientHeight * 100 / el.scrollHeight) : 100;

    const minHeightPercent = (minSize * 100 / el.clientHeight);
    const minWidthPercent = (minSize * 100 / el.clientWidth);

    heightPercent = Math.max(heightPercent, minHeightPercent);
    widthPercent = Math.max(widthPercent, minWidthPercent);

    sizeHeight.value = heightPercent < 100 && el.scrollHeight > minSize ? `${heightPercent}%` : '';
    sizeWidth.value = widthPercent < 100 && el.scrollWidth > minSize ? `${widthPercent}%` : '';

    gutter.value = getScrollbarWidth();

    emit('overflow-update', isHeightOverflow, isWidthOverflow);
}

function handleScroll(e) {
    updateScrollPosition();
    emit('scroll', e);
}

function setPosition(top, left) {
    if (wrap.value) {
        if (top != null) wrap.value.scrollTop = top;
        if (left != null) wrap.value.scrollLeft = left;
    }
}

defineExpose({
    update,
    setPosition,
});

onMounted(() => {
    if (props.native) return;
    nextTick(update);
    if (!props.noresize) {
        resizeObserver = new ResizeObserver(update);
        resizeObserver.observe(wrap.value);
        resizeObserver.observe(resize.value);
    }
});

onBeforeUnmount(() => {
    if (props.native || props.noresize || !resizeObserver) return;
    resizeObserver.disconnect();
});
</script>

<style scroped lang="postcss">
.el-scrollbar {
    overflow: hidden;
    position: relative;

    &:hover,
    &:active,
    &:focus {
        > .el-scrollbar__bar {
            opacity: 1;
        }
    }

    &__wrap {
        max-height: 100vh;
        overflow-y: auto;
        overflow-x: hidden;

        &--x-axis {
            overflow-x: scroll;
            overflow-y: hidden;
        }
        &--y-axis {
            overflow-x: hidden;
            overflow-y: scroll;
        }
        &--axis {
            overflow: scroll;
            scrollbar-color: rgba(0, 0, 0, 0) rgba(0, 0, 0, 0);
            scrollbar-width: none;
            -ms-overflow-style: none;
        }

    }
    &__thumb {
        position: relative;
        display: block;
        width: 0;
        height: 0;
        cursor: pointer;
        border-radius: inherit;
        background-color: var(--av-scroll-thumb);
    }
    &__bar {
        position: absolute;
        right: 2px;
        bottom: 2px;
        border-radius: 3px;
        z-index: 40;
        opacity: 0;
        transition: opacity 120ms ease-out;
        &.is-vertical {
            width: 6px;
            top: 2px;
        }
    }
}
.el-scrollbar__bar.is-vertical>div {
    width: 100%;
}
</style>
